import React, { useState, useEffect, useCallback, useRef } from 'react';
import { v4 as uuidv4 } from 'uuid';
import aiService from 'api/services/ai';
import whiteGridLogo from 'assets/images/logos/white-grid-logo.png';
import { MessageRenderer } from './MessageRenderer';
import './Gigabot.scss';

/**
 * Reusable Gigabot Chat Component
 *
 * Props:
 * - isOpen: boolean - Controls visibility of the chat panel
 * - onClose: function - Callback when chat is closed
 * - onOpen: function - Callback when chat is opened (for mobile FAB)
 * - placeholder: string - Input placeholder text
 * - containerElement: string - HTML element type ('div', 'aside', etc.)
 * - ariaProps: object - Additional ARIA properties
 * - initialMessage: object - Initial welcome message (optional)
 * - className: string - Additional CSS classes
 * - style: object - Inline styles for textarea (optional)
 * - prefilledContent: string - Content to prefill in the input (optional)
 * - context: string - Page context for tailored AI responses (optional)
 * - contextData: object - Context-specific data to send with chat messages (optional)
 * - showMobileFAB: boolean - Whether to show the mobile floating action button
 * - sessionId: string - External session ID to use (optional, generates new if not provided)
 */
export const Gigabot = ({
  isOpen = false,
  onClose = () => {},
  onOpen = () => {},
  placeholder = 'Ask me anything...',
  containerElement = 'div',
  ariaProps = {},
  initialMessage = null,
  className = '',
  style = {},
  prefilledContent = '',
  prefilledTrigger = 0,
  onMessage = null,
  onUserInput = null,
  context = null,
  contextData = null,
  showMobileFAB = true,
  resizable = false,
  minWidth = 320,
  maxWidth = 800,
  defaultWidth = 480,
  onResize = null,
  sessionId = null, // External session ID (optional)
}) => {
  // Gigabot state
  const [gbMessages, setGbMessages] = useState(() => {
    if (initialMessage) {
      return [initialMessage];
    }
    return [
      {
        id: 1,
        type: 'assistant',
        content:
          "Hello! I'm Gigabot, your AI assistant. How can I help you today?",
        timestamp: new Date(),
      },
    ];
  });

  // Handler for button clicks in messages
  const handleMessageButtonClick = (button) => {
    if (button.action && button.value) {
      // Set the input with the template
      setGbInput(button.value);

      // Focus the input for user to review and send
      setTimeout(() => gbInputRef.current?.focus(), 0);
    }
  };

  // Handler to move cursor to next blank when send is clicked
  const moveToNextBlank = () => {
    const currentContent = gbInput;
    const firstBlankIndex = currentContent.indexOf('_____');

    if (firstBlankIndex === -1) return false; // No blanks found

    // Replace the first blank with empty string
    const before = currentContent.substring(0, firstBlankIndex);
    const after = currentContent.substring(firstBlankIndex + 5);
    const newContent = before + after;

    setGbInput(newContent);

    // Focus and set cursor position at the blank location
    setTimeout(() => {
      if (gbInputRef.current) {
        gbInputRef.current.focus();
        gbInputRef.current.setSelectionRange(firstBlankIndex, firstBlankIndex);
      }
    }, 0);

    return true; // Blank was found and processed
  };

  const [gbInput, setGbInput] = useState(prefilledContent);
  const [gbLoading, setGbLoading] = useState(false);
  const [gbSessionId] = useState(() => sessionId || uuidv4()); // Use external session ID if provided
  const [panelWidth, setPanelWidth] = useState(defaultWidth);
  const [isResizing, setIsResizing] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const gbMessagesEndRef = useRef(null);
  const gbInputRef = useRef(null);
  const gbEventSourceRef = useRef(null);
  const resizeStartX = useRef(0);
  const resizeStartWidth = useRef(0);
  const dropdownRef = useRef(null);

  // Utility functions
  const formatTime = useCallback((ts) => {
    if (!ts) return '';
    return new Date(ts).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
    });
  }, []);

  /**
   * Extract structured data from context_data for enhanced rendering
   */
  const extractStructuredData = useCallback((contextData) => {
    if (!contextData) return null;

    const structured = {};

    // Extract milestones
    if (contextData.milestones && Array.isArray(contextData.milestones)) {
      structured.milestones = contextData.milestones;
    }

    // Extract skills
    if (
      contextData.requiredSkills &&
      Array.isArray(contextData.requiredSkills)
    ) {
      structured.skills = contextData.requiredSkills;
    } else if (contextData.skills && Array.isArray(contextData.skills)) {
      structured.skills = contextData.skills;
    }

    // Extract budget
    if (contextData.budget) {
      structured.budget = contextData.budget;
    } else if (contextData.amountUsd) {
      structured.budget = {
        value: contextData.amountUsd.value || contextData.amountUsd,
        confidence: contextData.amountUsd.confidence || 1.0,
      };
    }

    // Only return if we have structured data
    return Object.keys(structured).length > 0 ? structured : null;
  }, []);

  const resizeTextarea = useCallback((textarea) => {
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = Math.min(textarea.scrollHeight, 200) + 'px';
    }
  }, []);

  // Gigabot handlers
  const handleGbSend = async () => {
    const msg = gbInput.trim();
    if (!msg || gbLoading) return;

    // Check if there are blanks to fill - if so, move to next blank instead of sending
    if (gbInput.includes('_____')) {
      moveToNextBlank();
      return;
    }

    const userMessage = {
      id: Date.now(),
      type: 'user',
      content: msg,
      timestamp: new Date(),
    };

    setGbMessages((prev) => [...prev, userMessage]);
    setGbInput('');
    setGbLoading(true);

    // Notify parent component of user input
    if (onUserInput) {
      onUserInput(msg);
    }

    // Refocus after state update
    setTimeout(() => gbInputRef.current?.focus(), 0);

    // Streaming implementation
    await handleGbStreaming(userMessage.content);
  };

  const handleGbStreaming = async (message) => {
    // Close any existing stream
    if (gbEventSourceRef.current) {
      try {
        gbEventSourceRef.current.close();
      } catch (e) {
        console.warn('Error closing existing EventSource:', e);
      }
      gbEventSourceRef.current = null;
    }

    // Set a timeout to ensure loading state is reset even if streaming fails
    const loadingTimeout = setTimeout(() => {
      console.warn(
        'Gigabot: Streaming timeout reached, resetting loading state'
      );
      setGbLoading(false);
    }, 30000); // 30 second timeout

    try {
      const streamingMessage = {
        id: Date.now() + 1,
        type: 'assistant',
        content: '',
        timestamp: new Date(),
        isStreaming: true,
      };

      setGbMessages((prev) => [...prev, streamingMessage]);

      const callbacks = {
        onSessionUpdate: (sessionData) => {},
        onChunk: (chunkData) => {
          setGbMessages((prev) =>
            prev.map((msg) =>
              msg.id === streamingMessage.id
                ? {
                    ...msg,
                    content: chunkData.content,
                    isStreaming: !chunkData.isComplete,
                  }
                : msg
            )
          );
        },
        onStreamComplete: (completeData) => {
          clearTimeout(loadingTimeout);

          // Extract structured data from context_data
          const structuredData = extractStructuredData(
            completeData.context_data
          );

          const finalMessage = {
            id: streamingMessage.id,
            type: 'assistant',
            content: completeData.content,
            isStreaming: false,
            timestamp: completeData.timestamp
              ? new Date(completeData.timestamp)
              : streamingMessage.timestamp,
            // Include SDUI and context data if present
            sdui: completeData.sdui,
            context_data: completeData.context_data,
            structuredData: structuredData, // Add structured data for rendering
            sessionId: completeData.sessionId,
            messageId: completeData.messageId,
            model: completeData.model,
          };

          setGbMessages((prev) =>
            prev.map((msg) =>
              msg.id === streamingMessage.id ? finalMessage : msg
            )
          );

          // Notify parent component of new message with full response data
          if (onMessage) {
            onMessage(finalMessage);
          }

          setGbLoading(false);
        },
        onError: (error) => {
          console.error('Gigabot: Streaming error:', error);
          clearTimeout(loadingTimeout);

          setGbMessages((prev) =>
            prev.map((msg) =>
              msg.id === streamingMessage.id
                ? {
                    ...msg,
                    content: 'Sorry, I encountered an error. Please try again.',
                    isStreaming: false,
                  }
                : msg
            )
          );
          setGbLoading(false);
        },
      };

      await aiService.chat(message, {
        sessionId: gbSessionId,
        chatContext: context || 'default',
        contextData: contextData,
        model: null,
        stream: true,
        callbacks,
      });
    } catch (error) {
      console.error('Gigabot: Streaming error in try-catch:', error);
      clearTimeout(loadingTimeout);

      // Add error message
      const errorMessage = {
        id: Date.now() + 1,
        type: 'assistant',
        content: 'Sorry, I encountered an error. Please try again.',
        timestamp: new Date(),
        isStreaming: false,
      };

      setGbMessages((prev) => [...prev, errorMessage]);
      setGbLoading(false);
    }
  };

  const handleGbKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleGbSend();
    }
  };

  const handleGbInputChange = (e) => {
    setGbInput(e.target.value);
    resizeTextarea(e.target);
  };

  // Action dropdown handlers
  const toggleDropdown = (messageId) => {
    setActiveDropdown(activeDropdown === messageId ? null : messageId);
  };

  const handleDeleteMessage = (messageId) => {
    setGbMessages((prev) => prev.filter((msg) => msg.id !== messageId));
    setActiveDropdown(null);
  };

  // Close dropdown when clicking outside
  const handleClickOutside = useCallback((event) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
      setActiveDropdown(null);
    }
  }, []);

  // Auto-scroll Gigabot messages
  useEffect(() => {
    gbMessagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [gbMessages]);

  // Effect to auto-focus input when chat opens
  useEffect(() => {
    if (isOpen && gbInputRef.current) {
      gbInputRef.current.focus();
    }
  }, [isOpen]);

  // Effect to manage body scroll on mobile when chat is open
  useEffect(() => {
    if (isOpen) {
      document.body.classList.add('gigabot-mobile-open');
    } else {
      document.body.classList.remove('gigabot-mobile-open');
    }

    // Cleanup on unmount
    return () => {
      document.body.classList.remove('gigabot-mobile-open');
    };
  }, [isOpen]);

  // Effect to update input when prefilledContent changes (always overwrite)
  useEffect(() => {
    if (prefilledContent) {
      setGbInput(prefilledContent);
    }
  }, [prefilledContent, prefilledTrigger]);

  // Effect to update initial message when it changes (for loading states)
  useEffect(() => {
    if (initialMessage && gbMessages.length > 0 && gbMessages[0].id === initialMessage.id) {
      // Update the first message if it's the same ID (loading -> loaded transition)
      setGbMessages([initialMessage]);
    }
  }, [initialMessage]);

  // Effect to handle clicking outside dropdown
  useEffect(() => {
    if (activeDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [activeDropdown, handleClickOutside]);

  // Auto-resize textarea when gbInput changes programmatically
  useEffect(() => {
    resizeTextarea(gbInputRef.current);
  }, [gbInput, resizeTextarea]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (gbEventSourceRef.current) {
        try {
          gbEventSourceRef.current.close();
        } catch (e) {
          console.warn('Error closing EventSource on cleanup:', e);
        }
      }
    };
  }, []);

  // Store resize state in ref to avoid stale closures
  const isResizingRef = useRef(false);

  // Resize handlers
  const handleResizeMove = useCallback(
    (e) => {
      if (!isResizingRef.current) return;

      const deltaX = resizeStartX.current - e.clientX; // Negative because we're resizing from the left edge
      const newWidth = Math.min(
        maxWidth,
        Math.max(minWidth, resizeStartWidth.current + deltaX)
      );

      setPanelWidth(newWidth);

      // Notify parent component if callback provided
      if (onResize) {
        onResize(newWidth);
      }
    },
    [minWidth, maxWidth, onResize]
  );

  const handleResizeEnd = useCallback(() => {
    isResizingRef.current = false;
    setIsResizing(false);
    document.removeEventListener('mousemove', handleResizeMove);
    document.removeEventListener('mouseup', handleResizeEnd);
  }, [handleResizeMove]);

  const handleResizeStart = useCallback(
    (e) => {
      if (!resizable) return;

      e.preventDefault();
      isResizingRef.current = true;
      setIsResizing(true);
      resizeStartX.current = e.clientX;
      resizeStartWidth.current = panelWidth;

      // Add event listeners to document to handle mouse movement outside the panel
      document.addEventListener('mousemove', handleResizeMove);
      document.addEventListener('mouseup', handleResizeEnd);
    },
    [resizable, panelWidth, handleResizeMove, handleResizeEnd]
  );

  // Clean up resize listeners if component unmounts while resizing
  useEffect(() => {
    return () => {
      document.removeEventListener('mousemove', handleResizeMove);
      document.removeEventListener('mouseup', handleResizeEnd);
    };
  }, [handleResizeMove, handleResizeEnd]);

  // Dynamic container element
  const ContainerElement = containerElement;

  const defaultAriaProps = {
    role: 'dialog',
    'aria-modal': 'false',
    'aria-label': 'Gigabot chat panel',
    'aria-hidden': !isOpen,
  };

  const mergedAriaProps = { ...defaultAriaProps, ...ariaProps };

  const defaultStyle = {
    minHeight: '40px',
    maxHeight: '200px',
    resize: 'none',
    overflow: 'auto',
    border: 'none',
    outline: 'none',
    boxShadow: 'none',
  };

  const mergedStyle = { ...defaultStyle, ...style };

  const panelStyle = resizable
    ? {
        width: `${panelWidth}px`,
        maxWidth: '50vw', // Ensure width is applied
        transition: isResizing ? 'none' : 'width 220ms ease',
      }
    : {};

  return (
    <>
      {/* Mobile Floating Action Button */}
      {showMobileFAB && (
        <button
          className={`gigabot-fab ${isOpen ? 'hidden' : ''}`}
          onClick={onOpen}
          aria-label='Open Gigabot chat'
        >
          <img src={whiteGridLogo} alt='Gigabot' width='35' height='35' />
        </button>
      )}

      {/* Chat Panel */}
      <ContainerElement
        className={`gigabot-chat ${isOpen ? 'open' : ''} ${
          resizable ? 'resizable' : ''
        } ${isResizing ? 'resizing' : ''} ${className}`}
        style={panelStyle}
        {...mergedAriaProps}
      >
        {/* Resize Handle */}
        {resizable && (
          <div
            className='gigabot-resize-handle'
            onMouseDown={handleResizeStart}
            aria-label='Resize panel'
            role='separator'
            aria-orientation='vertical'
          />
        )}
        <div className='gigabot-header'>
          <h3>Gigabot</h3>
          <button
            className='gigabot-close'
            onClick={onClose}
            aria-label='Close Gigabot'
          >
            ×
          </button>
        </div>

        <div className='gigabot-messages'>
          {gbMessages.map((message) => (
            <div
              key={message.id}
              className={`gigabot-message ${
                message.type === 'user' ? 'user' : 'bot'
              }`}
            >
              <div className='gigabot-message-meta'>
                <div className='meta-left'>
                  <span className='sender'>
                    {message.type === 'user' ? '> you' : '> gigabot'}
                  </span>
                  <span className='time'>{formatTime(message.timestamp)}</span>
                </div>
                {message.type === 'assistant' && !message.isStreaming && (
                  <div className='message-actions' ref={dropdownRef}>
                    <button
                      className='action-button'
                      onClick={() => toggleDropdown(message.id)}
                      aria-label='Message actions'
                    >
                      <svg
                        width='16'
                        height='16'
                        viewBox='0 0 24 24'
                        fill='none'
                        stroke='currentColor'
                        strokeWidth='2'
                        strokeLinecap='round'
                        strokeLinejoin='round'
                      >
                        <circle cx='12' cy='12' r='1' />
                        <circle cx='12' cy='5' r='1' />
                        <circle cx='12' cy='19' r='1' />
                      </svg>
                    </button>
                    {activeDropdown === message.id && (
                      <div className='action-dropdown'>
                        <button
                          className='dropdown-item delete'
                          onClick={() => handleDeleteMessage(message.id)}
                        >
                          <svg
                            width='14'
                            height='14'
                            viewBox='0 0 24 24'
                            fill='none'
                            stroke='currentColor'
                            strokeWidth='2'
                            strokeLinecap='round'
                            strokeLinejoin='round'
                          >
                            <path d='M3 6h18' />
                            <path d='M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6' />
                            <path d='M8 6V4c0-1 1-2 2-2h4c-1 0 2 1 2 2v2' />
                          </svg>
                          Delete
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
              <div className='gigabot-message-content'>
                {message.isStreaming ? (
                  <div className='typing-indicator'>
                    <span>gigacore is thinking</span>
                    <div className='typing-dots'>
                      <span className='dot'></span>
                      <span className='dot'></span>
                      <span className='dot'></span>
                    </div>
                    {message.content && (
                      <div className='streaming-content'>
                        <MessageRenderer
                          content={message.content}
                          structuredData={message.structuredData}
                          buttons={message.buttons}
                          onButtonClick={handleMessageButtonClick}
                        />
                      </div>
                    )}
                  </div>
                ) : (
                  <div className='final-content'>
                    {message.content && (
                      <MessageRenderer
                        content={message.content}
                        structuredData={message.structuredData}
                        buttons={message.buttons}
                        onButtonClick={handleMessageButtonClick}
                      />
                    )}
                  </div>
                )}
              </div>
            </div>
          ))}
          <div ref={gbMessagesEndRef} />
        </div>

        <form
          className='gigabot-input'
          onSubmit={(e) => {
            e.preventDefault();
            handleGbSend();
          }}
        >
          <div className='gigabot-input-container'>
            <textarea
              ref={gbInputRef}
              placeholder={placeholder}
              aria-label='Gigabot message'
              value={gbInput}
              onChange={handleGbInputChange}
              onKeyDown={handleGbKeyDown}
              disabled={gbLoading}
              spellCheck='false'
              rows={1}
              style={mergedStyle}
            />
            <button
              type='submit'
              className='send-button'
              disabled={!gbInput.trim() || gbLoading}
              aria-label={
                gbInput.includes('_____') ? 'Next field' : 'Send message'
              }
            >
              {gbLoading ? (
                <div className='loading-spinner'></div>
              ) : gbInput.includes('_____') ? (
                // Arrow right icon for moving to next blank
                <svg
                  width='16'
                  height='16'
                  viewBox='0 0 24 24'
                  fill='none'
                  stroke='currentColor'
                  strokeWidth='2'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                >
                  <path d='M5 12h14' />
                  <path d='M12 5l7 7-7 7' />
                </svg>
              ) : (
                // Send icon for sending message
                <svg
                  width='16'
                  height='16'
                  viewBox='0 0 24 24'
                  fill='none'
                  stroke='currentColor'
                  strokeWidth='2'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                >
                  <path d='M22 2L11 13' />
                  <path d='M22 2L15 22L11 13L2 9L22 2Z' />
                </svg>
              )}
            </button>
          </div>
        </form>
      </ContainerElement>
    </>
  );
};
